import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lightbulb, Bug, Send, CheckCircle2 } from "lucide-react";

const AREAS = ["general", "math", "reading-writing", "oceanography", "history", "spanish", "creative-tech-art", "app / website"];

export default function Feedback() {
  const { user } = useAuth();
  const [category, setCategory] = useState("idea");
  const [area, setArea] = useState("general");
  const [message, setMessage] = useState("");
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async () => { const { data } = await api.get("/feedback"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Write your feedback first.");
    setSending(true);
    try {
      await api.post("/feedback", { category, area, message });
      toast.success("Thanks! Your feedback was sent.");
      setMessage("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const resolve = async (id) => {
    try { await api.post(`/feedback/${id}/resolve`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="feedback-page">
      <div>
        <div className="chip mb-2">Feedback</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Suggest an idea or report a problem</h1>
        <p className="text-slate-600 mt-1">Tell us what would make Oceanic Learn better — anything goes.</p>
      </div>

      <section className="p-6 rounded-3xl bg-white border border-slate-200">
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" data-testid="fb-cat-idea" onClick={() => setCategory("idea")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${category === "idea" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>
              <Lightbulb size={14} /> Suggest an idea
            </button>
            <button type="button" data-testid="fb-cat-issue" onClick={() => setCategory("issue")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${category === "issue" ? "bg-rose-500 text-white" : "bg-slate-100 text-slate-600"}`}>
              <Bug size={14} /> Report a problem
            </button>
          </div>
          <select data-testid="fb-area" value={area} onChange={(e) => setArea(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white">
            {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <textarea data-testid="fb-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
            placeholder={category === "idea" ? "I wish the app could…" : "Something isn't working: …"}
            className="w-full px-4 py-3 rounded-xl border border-slate-200" />
          <button data-testid="fb-submit" disabled={sending}
            className="px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <Send size={16} /> Send feedback
          </button>
        </form>
      </section>

      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">
          {user.role === "parent" ? "Feedback inbox" : "Your feedback"}
        </h2>
        <div className="grid gap-3" data-testid="feedback-list">
          {items.map((f) => (
            <div key={f.id} data-testid={`feedback-${f.id}`}
              className={`p-4 rounded-2xl border ${f.status === "resolved" ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
                  {f.category === "issue" ? <span className="text-rose-600 flex items-center gap-1"><Bug size={12} /> Problem</span> : <span className="text-teal-700 flex items-center gap-1"><Lightbulb size={12} /> Idea</span>}
                  <span className="text-slate-400">· {f.area}</span>
                </div>
                {user.role === "parent" && f.status !== "resolved" && (
                  <button data-testid={`fb-resolve-${f.id}`} onClick={() => resolve(f.id)} className="text-xs text-slate-500 hover:text-teal-700 flex items-center gap-1">
                    <CheckCircle2 size={14} /> Mark done
                  </button>
                )}
                {f.status === "resolved" && <span className="chip">Resolved</span>}
              </div>
              <div className="text-slate-800 mt-2">{f.message}</div>
              <div className="text-xs text-slate-400 mt-2">{f.user_name} ({f.role}) · {new Date(f.created_at).toLocaleDateString()}</div>
            </div>
          ))}
          {items.length === 0 && <div className="text-slate-500 text-sm">No feedback yet.</div>}
        </div>
      </section>
    </div>
  );
}
